import { Component, ErrorInfo, ReactNode, useEffect, useMemo, useState } from 'react'
import WaveSurfer from 'wavesurfer.js'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import colors from 'utils/colors'
import { icons } from 'utils/icons'
import { captureHandledError, getIsSentryEnabled } from 'utils/sentry'

import CustomIcon from 'components/icon/CustomIcon'
import CustomText from 'components/text/CustomText'

interface Props {
  url: string
}

const secondsToHms = (d: number) => {
  const m = Math.floor((d % 3600) / 60)
  const s = Math.floor((d % 3600) % 60)

  const mDisplay = m > 0 && m < 10 ? m : m
  const sDisplay = s >= 0 && s < 10 ? '0' + s : s
  return { mDisplay, sDisplay }
}

const Waveform = ({ url }: Props) => {
  const [playing, setPlaying] = useState(false)
  const [finish, setFinish] = useState(false)
  const [waveform, setWaveform] = useState<WaveSurfer | null>(null)
  // Keep the container id stable across re-renders so WaveSurfer can find it
  const uniqueId = useMemo(() => uuidv4(), [])

  useEffect(() => {
    // Guard: WaveSurfer.load throws "url parameter cannot be empty" for falsy urls.
    // This can happen for freshly selected audio files whose blob URL is not ready yet
    // (see Input.tsx localUrls effect), so we bail out until a valid url arrives.
    if (!url) return

    const ws = WaveSurfer.create({
      barGap: 2,
      height: 50,
      barWidth: 3,
      barRadius: 3,
      cursorWidth: 1,
      barMinHeight: 2,
      responsive: true,
      backend: 'WebAudio',
      progressColor: '#3d4f94',
      container: `#abc${uniqueId}`,
      waveColor: `${colors.mainVime}`,
      cursorColor: 'transparent',
      xhr: {
        credentials: 'include',
      },
    })
    ws.on('finish', () => {
      setFinish(true)
      setPlaying(false)
    })

    // Async decode/fetch failures emit via the 'error' event and would otherwise
    // become uncaught (XHR aborts, CORS, unsupported codec, etc.) — swallow them
    // here so a broken audio file doesn't take down the surrounding tree.
    ws.on('error', (err: unknown) => {
      console.error('WaveSurfer error:', err)
      setPlaying(false)
      setFinish(false)
    })

    try {
      ws.load(url)
    } catch (err) {
      console.error('Waveform load failed:', err)
    }

    setWaveform(ws)

    return () => {
      try {
        ws.destroy()
      } catch {
        // WaveSurfer may throw when destroyed mid-decoding; ignore.
      }
      setWaveform(null)
    }
  }, [url, uniqueId])

  const handlePlay = () => {
    if (!waveform) return
    setPlaying(!playing)
    setFinish(false)
    waveform.playPause()
  }

  const { mDisplay, sDisplay } = secondsToHms(
    waveform?.getDuration ? Math.floor(waveform.getDuration()) : 0,
  )

  // Placed AFTER all hooks to respect the Rules of Hooks (hook count must stay
  // stable across renders). Defensive belt-and-suspenders — parent components
  // already gate on a truthy url before rendering <Waveform />.
  if (!url) return null

  return (
    <WaveformContianer>
      <PlayButton onClick={handlePlay}>
        {(!playing || finish) && (
          <CustomIcon iconPreset="large" source={icons.sendActiveRecordPlay} />
        )}
        {playing && !finish && (
          <CustomIcon iconPreset="large" source={icons.sendActiveRecordPause} />
        )}
      </PlayButton>
      <Wave id={`abc${uniqueId}`} style={{ zIndex: 0 }} />
      <Timer>
        <CustomText typography="body7" color={colors.gray80}>
          {`${mDisplay}:`}
        </CustomText>
        <CustomText typography="body7" color={colors.gray80}>
          {`${sDisplay}`}
        </CustomText>
      </Timer>
    </WaveformContianer>
  )
}

// Local ErrorBoundary so a crash inside WaveSurfer (3rd-party audio decoding,
// React render-time errors thrown from event handlers, etc.) collapses ONLY
// the audio widget instead of bubbling up to the global Sentry boundary and
// blanking the whole screen.
interface WaveformBoundaryState {
  hasError: boolean
}

class WaveformErrorBoundary extends Component<{ children: ReactNode }, WaveformBoundaryState> {
  state: WaveformBoundaryState = { hasError: false }

  static getDerivedStateFromError(): WaveformBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Waveform crashed:', error, info.componentStack)
    if (getIsSentryEnabled()) {
      captureHandledError(error, {
        tags: { source: 'waveform.error_boundary' },
        extra: { componentStack: info.componentStack },
      })
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <WaveformContianer aria-label="audio-unavailable">
          <CustomText typography="body7" color={colors.gray80}>
            —
          </CustomText>
        </WaveformContianer>
      )
    }
    return this.props.children
  }
}

const SafeWaveform = (props: Props) => (
  <WaveformErrorBoundary>
    <Waveform {...props} />
  </WaveformErrorBoundary>
)

export default SafeWaveform

const Timer = styled.div`
  width: 28px;
  height: 12px;
  display: flex;
  align-self: center;
  flex-shrink: 0;
`
const WaveformContianer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 50px;
  width: 212px;
  background: transparent;
  border-radius: 10px;
  gap: 10px;
  border: 1px solid ${colors.gray60};
  padding: 0px 5px;
`

const Wave = styled.div`
  width: 100%;
  height: 100%;
`
const PlayButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: none;
  outline: none;
  cursor: pointer;
  padding-bottom: 3px;
`
